import {
  BasinStyle,
  BathroomConfigurationState,
  ValidationError,
  ValidationResult,
  ValidationRule,
} from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-validation.models';
import { BasinType } from 'src/app/models/configurators-2d/mobile/bathroom/bathroom-composition-dtos';
import { ProductAttribute } from 'src/app/models/configurators-2d/tunnel/bathroom/bathroom-tunnel-dtos';
import { BathroomConstraintDto } from 'src/app/models/configurators-2d/common-bathroom-dtos';
import {
  KentiaConstraintKeys,
  CommonConstraintKeys,
  BathroomConstraintDefaults,
} from 'src/app/consts/configurator-2d/mobile/bathroom/BathroomConstraintKeys.consts';

export const KENTIA_RANGE_ID = 'kentia';

/**
 * Interface pour les contraintes Kentia extraites
 * Ces contraintes sont partagées entre ValidationService et ConfigurationService
 */
export interface KentiaConstraints {
  maxToiletTopWidth: number;
  maxBasinPlanWidth: number;
  doubleBasinWidth: number;
  minFirstRowWidth: number;
  exceptionBasinPlanWidth;
}

/**
 * Extrait les contraintes Kentia depuis la liste des contraintes DTO
 * @param constraints Liste des contraintes récupérées depuis le service
 * @returns Les contraintes Kentia avec les valeurs ou les valeurs par défaut
 */
export function extractKentiaConstraints(constraints: BathroomConstraintDto[]): KentiaConstraints {
  const findConstraintValue = (key: string, defaultValue: number): number => {
    const constraint = constraints.find(c => c.key === key);
    return constraint ? parseFloat(constraint.value) : defaultValue;
  };

  return {
    maxToiletTopWidth: findConstraintValue(
      KentiaConstraintKeys.MAX_TOILET_TOP_WIDTH,
      BathroomConstraintDefaults.MAX_TOILET_TOP_WIDTH
    ),
    maxBasinPlanWidth: findConstraintValue(
      KentiaConstraintKeys.MAX_BASIN_PLAN_WIDTH,
      BathroomConstraintDefaults.MAX_BASIN_PLAN_WIDTH
    ),
    doubleBasinWidth: findConstraintValue(
      KentiaConstraintKeys.DOUBLE_BASIN_WIDTH,
      BathroomConstraintDefaults.DOUBLE_BASIN_WIDTH
    ),
    minFirstRowWidth: findConstraintValue(
      CommonConstraintKeys.MIN_FIRST_ROW_WIDTH,
      BathroomConstraintDefaults.MIN_FIRST_ROW_WIDTH
    ),
    exceptionBasinPlanWidth: findConstraintValue(
      KentiaConstraintKeys.EXCEPTION_BASIN_PLAN_WIDTH,
      BathroomConstraintDefaults.EXCEPTION_BASIN_PLAN_WIDTH
    ),
  };
}

/**
 * Crée les règles de validation Kentia avec les contraintes fournies
 * @param constraints Les contraintes Kentia (optionnel, utilise les valeurs par défaut si non fourni)
 * @returns Les règles de validation Kentia
 */
export function createKentiaRules(constraints?: KentiaConstraints): ValidationRule[] {
  const {
    maxToiletTopWidth = BathroomConstraintDefaults.MAX_TOILET_TOP_WIDTH,
    maxBasinPlanWidth: maxPlanVasqueWidth = BathroomConstraintDefaults.MAX_BASIN_PLAN_WIDTH,
    doubleBasinWidth = BathroomConstraintDefaults.DOUBLE_BASIN_WIDTH,
    minFirstRowWidth = BathroomConstraintDefaults.MIN_FIRST_ROW_WIDTH,
  } = constraints || {};

  return [
    {
      id: 'double_basin_requires_min_width',
      rangeId: KENTIA_RANGE_ID,
      name: 'Vasque double nécessite largeur minimale',
      description: `Une vasque double nécessite une largeur minimale de ${doubleBasinWidth}cm`,
      validate: (state: BathroomConfigurationState): ValidationResult => {
        if (state.selectedBasinType !== BasinType.Double) {
          return { valid: true, errors: [] };
        }

        const firstRow = state.composition?.compositionConfiguration?.rows?.find(r => r.index === 0);
        if (!firstRow) {
          return { valid: true, errors: [] };
        }

        const totalWidth = firstRow.columns?.reduce((sum, col) => sum + col.width, 0) || 0;

        if (totalWidth < doubleBasinWidth) {
          return {
            valid: false,
            errors: [
              {
                code: 'DOUBLE_BASIN_WIDTH',
                message: `Une vasque double nécessite une largeur de ${doubleBasinWidth}cm minimum`,
                field: 'basinType',
              },
            ],
          };
        }

        return { valid: true, errors: [] };
      },
    },
    {
      id: 'max_row_width',
      rangeId: KENTIA_RANGE_ID,
      name: 'Largeur maximale selon le style',
      description: `La somme des largeurs des colonnes ne peut pas dépasser ${maxPlanVasqueWidth}cm (plan vasque) ou ${maxToiletTopWidth}cm (vasque à poser)`,
      validate: (state: BathroomConfigurationState): ValidationResult => {
        const errors: ValidationError[] = [];
        const rows = state.composition?.compositionConfiguration?.rows || [];

        const maxWidth = state.selectedBasinStyle === BasinStyle.WashbasinTop ? maxPlanVasqueWidth : maxToiletTopWidth;
        const styleLabel =
          state.selectedBasinStyle === BasinStyle.WashbasinTop ? BasinStyle.WashbasinTop : 'plan de toilette';

        rows.forEach((row, rowIndex) => {
          const totalColumnWidth = row.columns?.reduce((sum, col) => sum + col.width, 0) || 0;

          if (totalColumnWidth > maxWidth) {
            errors.push({
              code: 'MAX_ROW_WIDTH_EXCEEDED',
              message: `La largeur totale (${totalColumnWidth}cm) dépasse la taille maximale du ${styleLabel} (${maxWidth}cm)`,
              field: 'columnWidth',
              rowIndex,
            });
          }
        });

        return {
          valid: errors.length === 0,
          errors,
        };
      },
    },
    {
      id: 'plan_vasque_first_column_drawer_only',
      rangeId: KENTIA_RANGE_ID,
      name: 'Plan vasque - première colonne tiroir uniquement',
      description:
        "Pour un plan vasque, l'évacuation d'eau se fait à gauche donc seul un tiroir est autorisé en première colonne",
      validate: (state: BathroomConfigurationState): ValidationResult => {
        if (state.selectedBasinStyle !== BasinStyle.WashbasinTop) {
          return { valid: true, errors: [] };
        }

        const firstRow = state.composition?.compositionConfiguration?.rows?.find(r => r.index === 0);
        if (!firstRow || !firstRow.columns || firstRow.columns.length === 0) {
          return { valid: true, errors: [] };
        }

        // Trier les colonnes par index pour identifier correctement la colonne la plus à gauche
        const sortedColumns = [...firstRow.columns].sort((a, b) => a.index - b.index);
        const firstColumn = sortedColumns[0];

        if (firstColumn.product?.attribute === ProductAttribute.Niche) {
          return {
            valid: false,
            errors: [
              {
                code: 'PLAN_VASQUE_FIRST_COLUMN_NO_NICHE',
                message:
                  "Pour un plan vasque, la première colonne de la première ligne doit être un tiroir (l'évacuation d'eau se fait à gauche)",
                field: 'columnType',
                rowIndex: 0,
                columnIndex: 0,
              },
            ],
          };
        }

        return { valid: true, errors: [] };
      },
    },
    {
      id: 'plan_vasque_120_double_drawers_only',
      rangeId: KENTIA_RANGE_ID,
      name: 'Plan vasque 120 double vasque - tiroirs uniquement',
      description: `En taille ${maxPlanVasqueWidth}, le plan vasque est un double vasque donc seuls des tiroirs sont autorisés sur la première ligne`,
      validate: (state: BathroomConfigurationState): ValidationResult => {
        if (state.selectedBasinStyle !== BasinStyle.WashbasinTop || state.selectedBasinType !== BasinType.Double) {
          return { valid: true, errors: [] };
        }

        const firstRow = state.composition?.compositionConfiguration?.rows?.find(r => r.index === 0);
        if (!firstRow || !firstRow.columns) {
          return { valid: true, errors: [] };
        }

        const totalWidth = firstRow.columns.reduce((sum, col) => sum + col.width, 0);

        if (totalWidth !== maxPlanVasqueWidth) {
          return { valid: true, errors: [] };
        }

        const errors: ValidationError[] = [];

        firstRow.columns.forEach((column, colIndex) => {
          if (column.product?.attribute === ProductAttribute.Niche) {
            errors.push({
              code: 'DOUBLE_VASQUE_NO_NICHE',
              message: `En configuration double vasque (${maxPlanVasqueWidth}cm), seuls les tiroirs sont autorisés sur la première ligne. La colonne ${colIndex + 1} est une niche.`,
              field: 'columnType',
              rowIndex: 0,
              columnIndex: colIndex,
            });
          }
        });

        return {
          valid: errors.length === 0,
          errors,
        };
      },
    },
    {
      id: 'vasque_a_poser_above_drawer_only',
      rangeId: KENTIA_RANGE_ID,
      name: 'Vasque à poser - positionnement au-dessus tiroir',
      description: "Une vasque à poser ne peut être positionnée qu'au-dessus d'un tiroir, pas au-dessus d'une niche",
      validate: (state: BathroomConfigurationState): ValidationResult => {
        if (state.selectedBasinStyle !== BasinStyle.CountertopBasin) {
          return { valid: true, errors: [] };
        }
        // Récupération de la bonne première ligne via l'index
        const firstRow = state.composition?.compositionConfiguration?.rows?.find(r => r.index === 0);

        if (!firstRow || !firstRow.columns || firstRow.columns.length === 0) {
          return { valid: true, errors: [] };
        }

        // Vérifier s'il y a au moins une colonne qui n'est pas une niche
        const hasDrawer = firstRow.columns.some(col => col.product?.attribute !== ProductAttribute.Niche);

        if (!hasDrawer) {
          return {
            valid: false,
            errors: [
              {
                code: 'VASQUE_A_POSER_ABOVE_NICHE',
                message: `La vasque à poser ne peut pas être positionnée au-dessus d'une niche. Changez la position de la vasque ou remplacez la niche par un tiroir.`,
                field: 'basinPosition',
                rowIndex: 0,
                columnIndex: 0,
              },
            ],
          };
        }

        return { valid: true, errors: [] };
      },
    },
  ];
}

/**
 * Règles de validation par défaut pour la gamme Kentia
 * Utilisé pour la rétrocompatibilité - préférer createKentiaRules() avec les contraintes du service
 */
export const KENTIA_RULES: ValidationRule[] = createKentiaRules();
